/**
 * EvidenceCategoriesPanel — admin ops panel for the evidence-based
 * capability-category assignment (versioned JSON, dry-run → receipt-gated
 * apply → rollback). Read/write only via the admin endpoints; never touches
 * published state, claims, slugs, verification, or Vault data.
 */
import React, { useEffect, useState } from "react";
import { Tags, Loader2, PlayCircle, Undo2, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../api";

function Num({ label, value, warn }) {
  return (
    <div style={{ minWidth: 110 }}>
      <div className="font-display text-2xl" style={{ color: warn ? "#dc2626" : "var(--ink)" }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="overline mt-0.5" style={{ color: "var(--ink-2)", fontSize: 10 }}>{label}</div>
    </div>
  );
}

export function EvidenceCategoriesPanel() {
  const [status, setStatus] = useState(null);
  const [dry, setDry] = useState(null);
  const [applyReceipt, setApplyReceipt] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [showCats, setShowCats] = useState(false);

  async function loadStatus() {
    try {
      const { data } = await api.get("/admin/evidence-categories/status");
      setStatus(data);
      if (data.last_apply) setApplyReceipt(data.last_apply);
    } catch { /* non-blocking */ }
  }
  useEffect(() => { loadStatus(); }, []);

  async function runDry() {
    setBusy("dry"); setErr("");
    try {
      const { data } = await api.post("/admin/evidence-categories/dry-run");
      setDry(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  }

  async function runApply() {
    if (!dry?.receipt_id) return;
    if (!window.confirm(`Apply evidence categories ${dry.version} to ${dry.docs_to_change} plant records? Only capability tags + evidence provenance are written. Nothing is unpublished, merged, or verified.`)) return;
    setBusy("apply"); setErr("");
    try {
      const { data } = await api.post("/admin/evidence-categories/apply", { receipt_id: dry.receipt_id });
      setApplyReceipt(data);
      await loadStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  }

  async function runRollback() {
    const rid = applyReceipt?.receipt_id;
    if (!rid) return;
    if (!window.confirm("Rollback the last apply? Prior capability values are restored from the snapshot.")) return;
    setBusy("rollback"); setErr("");
    try {
      const { data } = await api.post("/admin/evidence-categories/rollback", { apply_receipt_id: rid });
      setApplyReceipt(null);
      setDry(null);
      await loadStatus();
      window.alert(`Rollback complete — ${data.docs_restored} plant records restored.`);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  }

  const t = status?.totals;
  return (
    <section
      className="mt-5 cd-card p-5"
      style={{ borderLeft: "3px solid var(--orange)" }}
      data-testid="evidence-categories-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Tags size={18} style={{ color: "var(--orange)" }} />
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>
              EVIDENCE CATEGORIES · {status?.version?.toUpperCase() || "…"}
            </div>
            <div className="font-heading font-semibold text-sm mt-0.5">
              Evidence-based capability assignment — dry-run · receipt-gated apply · rollback
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={runDry}
            disabled={!!busy}
            className="btn btn-ghost btn-sm"
            data-testid="evcat-dryrun-btn"
          >
            {busy === "dry" ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Dry run
          </button>
          <button
            onClick={runApply}
            disabled={!!busy || !dry || !dry.reconciles || dry.docs_to_change === 0}
            className="btn btn-primary btn-sm"
            title={!dry ? "Run a dry-run first" : !dry.reconciles ? "Totals do not reconcile" : dry.docs_to_change === 0 ? "Already applied" : ""}
            data-testid="evcat-apply-btn"
          >
            {busy === "apply" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Apply {dry ? `(${dry.docs_to_change})` : ""}
          </button>
          {applyReceipt?.rollback_available !== false && applyReceipt?.receipt_id ? (
            <button
              onClick={runRollback}
              disabled={!!busy}
              className="btn btn-ghost btn-sm"
              style={{ color: "#dc2626", borderColor: "#fca5a5" }}
              data-testid="evcat-rollback-btn"
            >
              {busy === "rollback" ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Rollback
            </button>
          ) : null}
        </div>
      </div>

      {status ? (
        <div className="mt-3 font-mono text-xs" style={{ color: "var(--ink-2)" }} data-testid="evcat-status-line">
          Source: {t?.sheet_rows} sheet rows → {t?.eligible_rows} eligible (plant-specific + URL + text) · {t?.skipped_unconfirmed} unconfirmed skipped · {t?.unique_plants} plants · {status.taxonomy_categories} taxonomy categories · currently applied in DB: <strong style={{ color: "var(--ink)" }}>{status.applied_docs_in_db}</strong> plants
        </div>
      ) : null}

      {err ? (
        <div className="mt-3 font-mono text-xs flex items-center gap-2" style={{ color: "#dc2626" }} data-testid="evcat-error">
          <AlertTriangle size={13} /> {String(err)}
        </div>
      ) : null}

      {dry ? (
        <div className="mt-4 p-4" style={{ background: "#f8fafc", border: "1px solid var(--line)" }} data-testid="evcat-dryrun-result">
          <div className="overline mb-3">DRY-RUN RECEIPT · {dry.receipt_id} {dry.reconciles ? "· RECONCILES ✓" : "· DOES NOT RECONCILE ✕"}</div>
          <div className="flex flex-wrap gap-6">
            <Num label="ELIGIBLE ROWS" value={dry.eligible_rows} />
            <Num label="UNCONFIRMED SKIPPED" value={dry.skipped_unconfirmed} />
            <Num label="UNIQUE PLANTS" value={dry.unique_plants} />
            <Num label="FOUND IN DB" value={dry.plants_found_in_db} warn={dry.plants_found_in_db !== dry.unique_plants} />
            <Num label="MISSING" value={dry.plants_missing_from_db.length} warn={dry.plants_missing_from_db.length > 0} />
            <Num label="DUP CONFLICTS" value={dry.duplicate_conflicts} />
            <Num label="TO CHANGE" value={dry.docs_to_change} />
            <Num label="ALREADY APPLIED" value={dry.docs_already_applied} />
            <Num label="CATEGORIES ≥1" value={`${dry.categories_with_results}/${dry.categories_total}`} />
          </div>
          <button
            onClick={() => setShowCats((v) => !v)}
            className="font-mono text-xs mt-3 underline"
            style={{ color: "var(--ink-2)" }}
            data-testid="evcat-toggle-cats"
          >
            {showCats ? "Hide" : "Show"} category → plant counts
          </button>
          {showCats ? (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-x-6" data-testid="evcat-category-counts">
              {dry.category_counts.map((c) => (
                <div key={c.category} className="flex justify-between font-mono text-xs py-0.5" style={{ borderBottom: "1px dashed var(--line)" }}>
                  <span>{c.category}</span>
                  <span style={{ color: c.published_plants > 0 ? "var(--ink)" : "var(--ink-2)" }}>{c.published_plants}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {applyReceipt?.kind === "apply" ? (
        <div className="mt-3 font-mono text-xs p-3" style={{ background: "rgba(22,163,74,0.06)", border: "1px solid #86efac" }} data-testid="evcat-apply-receipt">
          WRITE RECEIPT · {applyReceipt.receipt_id} · {applyReceipt.created_at} — modified {applyReceipt.docs_modified} plants, {applyReceipt.docs_already_applied} already applied. Rollback snapshot {applyReceipt.rollback_available ? "saved" : "n/a"}.
        </div>
      ) : null}
    </section>
  );
}

export default EvidenceCategoriesPanel;
